/**
 * useShareCode Hook
 * Encode current config, decode pasted code, manage local presets
 * Persists presets to DB via API. localStorage is a read-only fallback for initial load.
 */

import { useState, useCallback, useEffect, useRef } from 'react';
import { TemplateConfig } from '@/types/template.types';
import { LocalPreset } from '@/types/shareCode.types';
import { encodeShareCode, decodeShareCode } from '@utils/shareCodec';
import { fetchWithCsrf } from '@utils/fetchWithCsrf';

const PRESETS_KEY = 'hookhouse_presets';
const API_BASE = '/api/share-presets';

interface UseShareCodeReturn {
  // Encoding
  encode: (config: TemplateConfig, name?: string) => string;

  // Decoding
  decode: (code: string) => {
    config: TemplateConfig;
    name?: string;
    version: number;
    timestamp: number;
  } | null;
  decodeError: string | null;

  // Presets
  presets: LocalPreset[];
  savePreset: (name: string, config: TemplateConfig) => void;
  deletePreset: (id: string) => void;
  renamePreset: (id: string, newName: string) => void;
}

function loadPresetsFromStorage(): LocalPreset[] {
  try {
    const stored = localStorage.getItem(PRESETS_KEY);
    return stored ? JSON.parse(stored) : [];
  } catch {
    return [];
  }
}

export function useShareCode(): UseShareCodeReturn {
  const [presets, setPresets] = useState<LocalPreset[]>([]);
  const [decodeError, setDecodeError] = useState<string | null>(null);
  const initialized = useRef(false);

  // Load presets from API on mount, fall back to localStorage
  useEffect(() => {
    if (initialized.current) return;
    initialized.current = true;

    (async () => {
      try {
        const res = await fetchWithCsrf(`${API_BASE}/list`);
        if (res.ok) {
          const json = await res.json();
          if (json.data && json.data.length > 0) {
            setPresets(json.data);
            return;
          }
        }
      } catch {
        // API unavailable
      }
      // Fallback: localStorage (read-only, populated by one-time migration)
      setPresets(loadPresetsFromStorage());
    })();
  }, []);

  // Update presets state (DB persistence happens in individual save/delete functions)
  const persistPresets = useCallback((updater: (prev: LocalPreset[]) => LocalPreset[]) => {
    setPresets(prev => updater(prev));
  }, []);

  const encode = useCallback((config: TemplateConfig, name?: string): string => {
    return encodeShareCode(config, name);
  }, []);

  const decode = useCallback((code: string) => {
    setDecodeError(null);
    try {
      const result = decodeShareCode(code.trim());
      return result;
    } catch (err: unknown) {
      setDecodeError(err instanceof Error ? err.message : 'Failed to decode share code');
      return null;
    }
  }, []);

  const savePreset = useCallback((name: string, config: TemplateConfig) => {
    const preset: LocalPreset = {
      id: `preset_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`,
      name,
      createdAt: new Date().toISOString(),
      config
    };
    persistPresets(prev => [preset, ...prev]);

    // Persist to API in background
    fetchWithCsrf(`${API_BASE}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ name, config }),
    }).then(async (res) => {
      if (res.ok) {
        const json = await res.json();
        // Update the optimistic entry with server-assigned id
        setPresets(prev => prev.map(p =>
          p.id === preset.id ? { ...p, id: json.data.id } : p
        ));
      }
    }).catch(() => {});
  }, [persistPresets]);

  const deletePreset = useCallback((id: string) => {
    persistPresets(prev => prev.filter(p => p.id !== id));

    fetchWithCsrf(`${API_BASE}/${id}`, { method: 'DELETE' }).catch(() => {});
  }, [persistPresets]);

  const renamePreset = useCallback((id: string, newName: string) => {
    persistPresets(prev => prev.map(p => p.id === id ? { ...p, name: newName } : p));

    // Note: no PUT endpoint for share presets — rename is localStorage-only
    // (the DB stores name at creation time)
  }, [persistPresets]);

  return {
    encode,
    decode,
    decodeError,
    presets,
    savePreset,
    deletePreset,
    renamePreset
  };
}
